import { type IReactionDisposer, makeAutoObservable, reaction, runInAction } from 'mobx'
import { nanoid } from 'nanoid'
import axios, { type CancelTokenSource, isCancel } from 'axios'
import { groupBy } from 'lodash'
import { uiStore } from 'shared/store/uiStore'
import { logger } from 'shared/lib'
import { type IResponseMessage } from 'entities/Message'
import { ContactsApi } from 'entities/Contacts'
import { Activity } from 'entities/Activity/model/Activity'
import { Message } from 'entities/Message/model/Message'
import { type IConversationsHistoryItem } from 'entities/Contacts/api/types'
import { type ContactsDetailsStore } from 'widgets/ContactsDetails'

export class ConversationsHistoryMessagesStore {
  private _per_page = 20
  private _page = 1

  private _itemsMap: Map<string | number, Message | Activity> = new Map()
  private _scrollBottomTrigger = ''

  private _loading = true
  private _loadingPrevious = false
  private _loadingMessages = false
  private _hasPrevious = true
  private _loadingBeforeId: string | number = 0
  private _handleReceiveNewScroll = false
  private _cancelTokenSource: CancelTokenSource | null = null
  private _disposeIsHideCalls: IReactionDisposer | null = null

  constructor(private _contactsDetailsStore: ContactsDetailsStore) {
    makeAutoObservable(this)
  }

  get entities() {
    return this._contactsDetailsStore.entities
  }

  get hasPrevious() {
    return this._hasPrevious
  }

  get scrollBottomTrigger() {
    return this._scrollBottomTrigger
  }

  get loadingPrevious() {
    return this._loadingPrevious
  }

  get loading() {
    return this._loading
  }

  get isShowActivity() {
    return this.entities.userSettingsStore.isUserShowActivity
  }

  get conversationId() {
    return this._contactsDetailsStore.conversation?.id
  }

  get allMessages() {
    const messages: Array<Message> = []
    Array.from(this._itemsMap.values()).forEach((item) => {
      if (item instanceof Message) {
        messages.push(item)
      }
    })
    return messages
  }
  get beforeId() {
    return this.items[0]?.id
  }
  get groupMessagesByDay() {
    return groupBy(this.items, 'day')
  }
  get items(): Array<Message | Activity> {
    return Array.from(this._itemsMap.values())
      .filter((message) => message instanceof Activity || !message.sending)
      .filter(
        (message) =>
          message instanceof Activity ||
          (message.type === 'call' ? message.record || message.body : true)
      )
      .sort((a, b) =>
        a.sortDateTime === b.sortDateTime
          ? Number(a.id) - Number(b.id)
          : a.sortDateTime - b.sortDateTime
      )
  }
  get isEmpty(): boolean {
    return this.items.length === 0
  }
  get messages(): Array<Message> {
    const messages: Array<Message> = []
    this.items.forEach((item) => {
      if (item instanceof Message) {
        messages.push(item)
      }
    })
    return messages
  }

  get loadingConversationsHistory() {
    return this._loadingMessages && this.isEmpty
  }

  get contact() {
    return this._contactsDetailsStore.contact
  }

  initCancelTokenSource = () => {
    this._cancelTokenSource?.cancel()
    this._cancelTokenSource = axios.CancelToken.source()
  }

  initReactions = () => {
    this.reactionIsHideCalls()
  }

  setHandleReceiveNewScroll = (value: boolean) => {
    this._handleReceiveNewScroll = value
  }

  reset = () => {
    this._loading = true
    this._loadingMessages = false
    this._page = 1
    this._loadingBeforeId = 0
    this._itemsMap.clear()
    this._disposeIsHideCalls?.()
  }

  loadMessages = async (page?: number) => {
    try {
      if (this._loadingMessages || !this.contact?.id) return

      runInAction(() => {
        this._loadingMessages = true
        this._loading = true
        this._page = page || 1
      })

      this.initCancelTokenSource()

      const { data } = await ContactsApi.getConversationsHistoryByContactId(
        {
          contactId: +this.contact.id,
          page: this._page,
          per_page: this._per_page,
          with_activity_logs: this.isShowActivity ? 1 : 0,
        },
        {
          cancelToken: this._cancelTokenSource?.token,
        }
      )

      if (data) {
        runInAction(() => {
          this.setItems(data.data)
          this._hasPrevious = data.meta.total > this.allMessages.length
        })
      }

      runInAction(() => {
        this._loadingMessages = false
        this._loading = false
      })
    } catch (e) {
      logger.error(e)

      if (isCancel(e)) {
        runInAction(() => {
          this._loadingMessages = true
          this._loading = true
        })
      } else {
        runInAction(() => {
          this._loadingMessages = false
          this._loading = false
        })
      }
    }
  }
  loadData = async () => {
    try {
      await this.loadMessages()
    } finally {
      this.setScrollBottomTrigger()
    }
  }

  refreshData = () => {
    this._page = 1
    this._itemsMap.clear()
    this.loadData()
  }

  async loadPreviousMessages(onSuccess: () => void) {
    if (this._loadingBeforeId === this.beforeId) return

    runInAction(() => {
      this._loadingBeforeId = this.beforeId
      this._loadingPrevious = true
    })

    try {
      await this.loadMessages(this._page + 1)

      onSuccess()
    } catch (e) {
      logger.error(e)
    } finally {
      runInAction(() => {
        this._loadingPrevious = false
      })
    }
  }
  setItems = (items: Array<IConversationsHistoryItem>) => {
    const iItems = items.map((item) => {
      const teamName = item.team?.name || 'All inboxes'
      const teamId = item.team?.id || 0

      if (item.conversation_activity_log) {
        return new Activity(
          {
            item: item.conversation_activity_log,
            conversationsHistoryInfo: {
              is_external:
                this.isContactsPage ||
                this.conversationId !== item.conversation_activity_log.conversation_id,
              teamName,
              teamId,
            },
          },
          this.entities
        )
      }

      return this.addItem(item.message, item.message.id, teamName, teamId)
    })

    let links: string[] = []

    try {
      iItems.forEach((item) => {
        if (item instanceof Message) {
          links = links.concat(item.links)
          this._itemsMap.set(item.client_id, item)
        } else {
          this._itemsMap.set(item.id, item)
        }
      })
    } catch (e) {
      logger.error(e)
    }

    if (this._handleReceiveNewScroll) {
      this.setScrollBottomTrigger()
    }
    this.entities.attachmentStore.checkPreviousAttachment(links)
  }
  addItem = (
    item: IResponseMessage,
    client_id: string | number,
    teamName: string,
    teamId: number
  ) => {
    const message = new Message(
      {
        message: { ...item, client_id },
        conversationsHistoryInfo: {
          is_external: this.isContactsPage || this.conversationId !== item.conversation_id,
          teamName,
          teamId,
        },
      },
      this.entities
    )

    if (Array.isArray(item.pending_mentions) && item.pending_mentions.length) {
      item.pending_mentions.forEach((item) => {
        this.entities.usersStore.addItem(item)
      })
    }

    return message
  }

  get isContactsPage() {
    return uiStore.pathName.includes('contacts')
  }

  setScrollBottomTrigger = () => {
    this._scrollBottomTrigger = nanoid()
  }

  reactionIsHideCalls = () => {
    this._disposeIsHideCalls?.()
    this._disposeIsHideCalls = reaction(
      () => this.entities.userSettingsStore.isHideCalls,
      () => {
        this.refreshData()
      },
      {
        fireImmediately: true,
      }
    )
  }
}
